import React, { useCallback, useEffect, useRef, useState } from 'react'
import { SlideLinkType, type Slide } from 'api/api.types'
import { Arrow } from 'components/Utility/Arrow/Arrow'
import { Slider } from 'components/Utility/Slider/Slider'
import { Picture } from 'components/Utility/Media/Picture'
import styles from './MainPage.module.scss'

const MOBILE_MAX_WIDTH = 428
const MOBILE_MIN_WIDTH = 310
const DESKTOP_MIN_WIDTH = 1024 - 60
const DESKTOP_MAX_WIDTH = 1200 - 60
const DEFAULT_WIDTH = 1140

const fitSliderWidth = (width: number) => (width > MOBILE_MAX_WIDTH
  ? Math.min(Math.max(width, DESKTOP_MIN_WIDTH), DESKTOP_MAX_WIDTH)
  : Math.min(Math.max(width, MOBILE_MIN_WIDTH), MOBILE_MAX_WIDTH))

const getSlideLink = (slide: Slide) => (slide.link.type === SlideLinkType.external
  ? slide.link.externalLink
  : slide.link.linkedPage?.fullPath)

type MainSlidesProps = {
  slides: Slide[],
}

/** Banner slider at the top of the main page */
export const MainSlides: React.FC<MainSlidesProps> = React.memo(({ slides }) => {
  const [page, setPage] = useState(0)
  const [slidesWidth, setSlidesWidth] = useState(DEFAULT_WIDTH)
  const sliderContainerRef = useRef<HTMLDivElement>(null)
  const resizeDebounceTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  const isFirstPage = page === 0
  const isLastPage = page >= slides.length - 1

  const prevPage = useCallback(() => {
    if (!isFirstPage) {
      setPage((current) => current - 1)
    }
  }, [isFirstPage])

  const nextPage = useCallback(() => {
    if (!isLastPage) {
      setPage((current) => current + 1)
    }
  }, [isLastPage])

  useEffect(() => {
    const measure = () => {
      if (sliderContainerRef.current) {
        setSlidesWidth(fitSliderWidth(sliderContainerRef.current.clientWidth))
      }
    }
    measure()
    const handleWindowResize = () => {
      clearTimeout(resizeDebounceTimer.current)
      resizeDebounceTimer.current = setTimeout(measure, 50)
    }
    window.addEventListener('resize', handleWindowResize, { passive: true })
    return () => {
      clearTimeout(resizeDebounceTimer.current)
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  return (
    <div className={styles.sliderContainer}>
      <div className={styles.arrows}>
        {!isFirstPage && <Arrow onClick={prevPage} className={styles.arrowLeft} bigOne direction={'left'} />}
        {!isLastPage && <Arrow onClick={nextPage} className={styles.arrowRight} bigOne direction={'right'} />}
      </div>
      <div className={styles.slider} ref={sliderContainerRef}>
        <Slider sense={50} page={page} onPageChange={setPage} itemWidth={slidesWidth}>
          {slides.map((slide, index) => (
            <div key={index} className={styles.slide}>
              <Picture
                desktopType={'fullscreen-desktop'}
                mobileType={'fullscreen-mobile'}
                className={styles.image}
                image={slide.image?.image}
                link={getSlideLink(slide)}
                alt={slide.image?.type === 'image' ? slide.image.imageAlt : undefined}
                lazy={index !== 0}
                preload={index === 0}
              />
            </div>
          ))}
        </Slider>
      </div>
    </div>
  )
})
MainSlides.displayName = 'MainSlides'
